import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';

import { User } from '../../../api/userApi/UserApi';

import { ManagementListSkeleton } from './ManagementListSkeleton';
import { Pagination } from '../../../common/components/Pagination';
import { PrimaryButton } from '../../../common/elements/Buttons/PrimaryButton';
import { Field, Checkbox } from '../../../common/components/Form';
import {
  ContentCard,
  TableCard,
  StyledTable,
  StyledTableHead,
  StyledTableBody,
  PaginationWrapper,
  EmptyState,
  Badge,
  ModalOverlay,
  ModalContent,
  ModalTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormCheckbox,
  ModalActions,
  ContentCardTitle,
  StyledFilterForm,
  StyledFilterCheckboxGroup,
  StyledFilterButtonWrapper
} from '../styles';

type UsersListProps = {
  users: User[];
  count: number;
  loading: boolean;
  page: number;
  offset: number;
  limit: number;
  onPagination: (offset: number, page: number) => void;
  onLimitChange: (limit: number) => void;
  userDetail: User | null;
  onOpenDetail: (user: User) => void;
  onCloseDetail: () => void;
};

type UserDetailProps = {
  user: User;
  onClose: () => void;
};

type UsersFilterFormData = {
  name: string;
  username: string;
  email: string;
  onlyActive: boolean;
  onlyInactive: boolean;
  allUsers: boolean;
};

type UsersFilterProps = {
  onSubmit: (data: UsersFilterFormData) => void;
};

export const UsersManager: React.FC<UsersListProps> = ({
  users,
  count,
  loading,
  page,
  offset,
  limit,
  onPagination,
  onLimitChange,
  onOpenDetail,
  onCloseDetail,
  userDetail
}) => {
  const renderCellValue = (col: any, row: any) => {
    if (col.render) return col.render(row[col.key], row);
    const value = row[col.key];
    if (col.key === 'is_active' || col.key === 'is_staff') {
      //return <Badge active={!!value}>{value ? 'Ano' : 'Ne'}</Badge>;
      return <Badge active={true}>Ano</Badge>;
    }
    return value ?? '—';
  };

  return (
    <>
      {/*<UsersListFilter onSubmit={(data) => console.log(data)} />*/}

      <ContentCard>
        <ContentCardTitle>Uživatelé</ContentCardTitle>

        <TableCard>
          <StyledTable>
            <StyledTableHead>
              <tr>
                <th key="id">ID</th>
                <th key="first_name">Jméno</th>
                <th key="last_name">Příjmení</th>
                <th key="email">Email</th>
                <th key="username">Uživatelské jméno</th>
                <th key="is_active">Aktivní</th>
                <th key="roles">Oprávnění</th>
              </tr>
            </StyledTableHead>
            <StyledTableBody>
              {loading ? (
                <ManagementListSkeleton columns={9} />
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState>Žádné záznamy</EmptyState>
                  </td>
                </tr>
              ) : (
                users.map((row) => (
                  <tr key={row.id} onClick={() => onOpenDetail(row)} style={{ cursor: 'pointer' }}>
                    <td>{row.id}</td>
                    <td>{row.first_name}</td>
                    <td>{row.last_name}</td>
                    <td>{row.email}</td>
                    <td>{row.username}</td>
                    <td>{renderCellValue({ key: 'is_active' }, row)}</td>
                    <td>{row.roles?.map(r => r.description || r.name).join(', ')}</td>
                  </tr>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </TableCard>

        {/*count > 0 && (
          <PaginationWrapper>
            <Pagination
              count={count}
              offset={offset}
              page={page}
              limit={limit}
              onSubmit={onPagination}
              onLimitChange={onLimitChange}
            />
          </PaginationWrapper>
        )*/}
      </ContentCard>

      {userDetail && <UserDetailModal user={userDetail} onClose={onCloseDetail} />}
    </>
  );
};

const UserDetailModal: React.FC<UserDetailProps> = ({ user, onClose }) => {
  return (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{`Detail uživatele: ${user.first_name} ${user.last_name}`}</ModalTitle>
        <form>
          <FormGroup key="id">
            <FormLabel htmlFor={`field-id`}>ID</FormLabel>
            <FormInput id={`field-id`} value={user.id ?? ''} readOnly />
          </FormGroup>
          <FormGroup key="name">
            <FormLabel htmlFor={`field-name`}>Jméno</FormLabel>
            <FormInput id={`field-name`} value={user.first_name ?? ''} readOnly />
          </FormGroup>
          <FormGroup key="last_name">
            <FormLabel htmlFor={`field-last_name`}>Příjmení</FormLabel>
            <FormInput id={`field-last_name`} value={user.last_name ?? ''} readOnly />
          </FormGroup>
          <FormGroup key="email">
            <FormLabel htmlFor={`field-email`}>Email</FormLabel>
            <FormInput id={`field-email`} value={user.email ?? ''} readOnly />
          </FormGroup>
          <FormGroup key="username">
            <FormLabel htmlFor={`field-username`}>Uživatelské jméno</FormLabel>
            <FormInput id={`field-username`} value={user.username ?? ''} readOnly />
          </FormGroup>

          <ModalActions>
            <PrimaryButton type="button" onClick={onClose}>
              Zavřít
            </PrimaryButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

const UsersListFilter: React.FC<UsersFilterProps> = ({ onSubmit }) => {
  const methods = useForm<UsersFilterFormData>({
    defaultValues: {
      name: '',
      username: '',
      email: '',
      onlyActive: false,
      onlyInactive: false,
      allUsers: true
    }
  });

  const { watch, setValue } = methods;
  const allUsers = watch('allUsers');
  const onlyActive = watch('onlyActive');
  const onlyInactive = watch('onlyInactive');

  useEffect(() => {
    if (allUsers) {
      setValue('onlyActive', false);
      setValue('onlyInactive', false);
    }
  }, [allUsers, setValue]);

  useEffect(() => {
    if (onlyActive) {
      setValue('allUsers', false);
      setValue('onlyInactive', false);
    }
  }, [onlyActive, setValue]);

  useEffect(() => {
    if (onlyInactive) {
      setValue('allUsers', false);
      setValue('onlyActive', false);
    }
  }, [onlyInactive, setValue]);

  const handleSubmit = (data: UsersFilterFormData) => {
    const params: any = {};

    if (data.name) {
      params.name = data.name;
    }

    if (data.username) {
      params.username = data.username;
    }

    if (data.email) {
      params.email = data.email;
    }

    if (data.onlyActive) {
      params.active = true;
    } else if (data.onlyInactive) {
      params.active = false;
    }

    onSubmit(params);
  };

  return (
    <FormProvider {...methods}>
      <StyledFilterForm onSubmit={methods.handleSubmit(handleSubmit)}>
        <Row>
          <Col md={4}>
            <Field id="user_name" name="user_name" label="Jméno uživatele" placeholder="" />
          </Col>
          <Col md={4}>
            <Field id="username" name="username" label="Uživatelské jméno" placeholder="" />
          </Col>
          <Col md={4}></Col>
        </Row>
        <Row>
          <Col md={4}>
            <Field id="email" name="email" label="Email" placeholder="" />
          </Col>
          <Col md={4}>
            <StyledFilterCheckboxGroup>
              <Checkbox id="allUsers" name="allUsers" label="všichni uživatelé" />
              <Checkbox id="onlyActive" name="onlyActive" label="pouze aktivní" />
              <Checkbox id="onlyInactive" name="onlyInactive" label="pouze neaktivní" />
            </StyledFilterCheckboxGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <StyledFilterButtonWrapper>
              <PrimaryButton type="submit">Hledat</PrimaryButton>
            </StyledFilterButtonWrapper>
          </Col>
        </Row>
      </StyledFilterForm>
    </FormProvider>
  );
};
